import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useT } from '../i18n/useT';
import DataCheckSheet from '../components/red/DataCheckSheet';
import { F, mr, DUR, EASE } from '../styles/theme';
import { usePress } from '../components/red/Press';
import { useTheme } from '../context/ThemeContext';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { useCurrency } from '../context/CurrencyContext';
import { getTxLabel, txDisplayCategory } from '../utils/transactionTypes';
import { buildTransferMap, isOpenTransfer } from '../utils/transferMatcher';
import { buildDataCheck } from '../utils/dataCheck';
import { mergeTxsByDay } from '../utils/exportData';
import { buildTxView, resolveScamDust, txValueFromMarket, isCryptoWalletTransfer } from '../utils/txView';
import { useHistoricalPrices } from '../hooks/useHistoricalPrices';
import { TxCard, TransferChainCard, buildGroupedChains, formatDateHeader } from '../components/TxCards';
import TxRow from '../components/TxRow';
import { useTransferLinking } from '../hooks/useTransferLinking';
import { useTimelineReorder } from '../hooks/useTimelineReorder';
import LinkTransferSheet from '../components/LinkTransferSheet';
import { Reorder, useDragControls } from 'framer-motion';
import TransactionsFab, { IconLink, IconSort, IconTrash, IconX, IconCheck, IconReset } from '../components/TransactionsFab';
import { Segmented } from '../components/red/Editorial';
import KebabMenu from '../components/red/KebabMenu';
import { Icon, Icons } from '../components/red/Icons';

// Fiat-Ein-/Auszahlungen (EUR/USD … Bank↔Börse) und NFT-Bewegungen sind keine
// Krypto-Bestandsereignisse. FIFO/Portfolio-Berechnungen lassen wir unverändert —
// diese Helfer wirken NUR auf Display/Filter.
const FIAT_SET = new Set(['EUR', 'USD', 'GBP', 'CHF', 'AUD', 'CAD', 'JPY', 'TRY', 'BRL']);

// Fiat-Geldfluss (Bank↔Börse). Eigene sichtbare Kategorie „Ein-/Auszahlung €" —
// erscheint im „Alle"-Tab UND im dedizierten Fiat-Tab, aber NICHT in den
// Krypto-Einzahlung/-Auszahlung-Tabs (klare Trennung) oder im Sonstige-Tab.
function isFiatTransfer(tx) {
  const isTransfer = tx.typ === 'einzahlung' || tx.typ === 'auszahlung';
  if (!isTransfer) return false;
  return FIAT_SET.has((tx.coin || tx.sym || '').toUpperCase());
}

// NFT-Bewegungen (Asset Recovery NFT, Airdrop NFT) — kein fungibles Asset.
// Bleiben (wie interne Gebühren) im Sonstige-Tab versteckt.
function isNftTransfer(tx) {
  const isTransfer = tx.typ === 'einzahlung' || tx.typ === 'auszahlung';
  if (!isTransfer) return false;
  return (tx.coin || tx.sym || '').toUpperCase() === 'NFT';
}

// Trading-Fee-TXs (typ='gebuehr') sind interne Buchungseinheiten fuer den
// FIFO-Konsum (BNB-Discount-Fees, Withdrawal-Fees etc.). Sie haben keinen
// EUR-Preis, machen die TX-Liste unleserlich und werden vom Engine ohnehin
// automatisch verarbeitet. Aus der User-sichtbaren TX-Liste ausblenden.
function isInternalFeeTx(tx) {
  return tx.typ === 'gebuehr';
}

// Scam-Dust (Address-Poisoning): on-chain als typ=pay/receiver-rewards=0 +
// Impersonation-„note" verifiziert. `_scamDust` ist der billige Read-Layer-
// Kandidat (canonicalNormalizer); die finale Entscheidung ist WERT-basiert
// (resolveScamDust + aktueller Preis) und passiert in der Komponente (isDust),
// damit echte Mini-Eingaenge teurer Coins nicht versteckt werden. Aus Haupt-/
// Krypto-Tabs raus, landet im „Sonstige"-Tab mit Badge. Bestandsneutral.

// „Sonstige" = technische, in der normalen Liste ausgeblendete Einträge:
// interne Handelsgebühren (gebuehr) und NFT-Bewegungen. Fiat NICHT mehr —
// Fiat hat jetzt die eigene sichtbare Kategorie „Ein-/Auszahlung €".
function isHiddenTx(tx) {
  return isNftTransfer(tx) || isInternalFeeTx(tx);
}

// ── WalletRow — thin wrapper around shared TxRow (variant="card") ──
// Display-Logik + visuelle Sprache leben in components/TxRow.js, der
// View-State (Type-Label, Flow, Wallet-Kette, isDerived, Unmatched) in
// utils/txView.js. Keine Berechnungs-Änderungen.
function WalletRow({ tx, marketData, onEdit, onDelete, transferMap, derivedValues, flat = true }) {
  const { locale } = useCurrency();
  const view = useMemo(
    () => buildTxView(tx, { transferMap, derivedValues, marketData, locale }),
    [tx, transferMap, derivedValues, marketData, locale]
  );
  return (
    <TxRow
      variant="card"
      view={view}
      marketData={marketData}
      flat={flat}
      rowRadius={3}
      rowShadow="none"
      expandable
      onEdit={onEdit}
      onDelete={onDelete}
      onClick={() => onEdit(tx)}
    />
  );
}


// ── Typ-Filter-Kategorien ────────────────────────────────────────────────────

// Tab-Leiste = Anzeige-KATEGORIEN (txDisplayCategory), NICHT roher Engine-`typ`.
// So liegen beide Beine eines Transfers in EINEM „Transfers"-Tab statt getrennt
// unter Ein-/Auszahlung, und gegenpartlose Zuflüsse (Airdrop/Reward/Staking)
// sammeln sich unter „Zuflüsse". Fiat (Bank↔Börse) und Sonstige (technische
// Einträge) bleiben Sonderfälle mit eigener Erkennung (isFiatTransfer/isHiddenTx).
const FILTER_TABS = [
  { id: 'all',          labelKey: 'all_transactions' },
  { id: 'kauf',         labelKey: 'buy' },
  { id: 'verkauf',      labelKey: 'sell' },
  { id: 'swap',         labelKey: 'swap' },
  { id: 'gesendet',     labelKey: 'wal_tab_sent' },
  { id: 'empfangen',    labelKey: 'wal_tab_received' },
  { id: 'transfer',     labelKey: 'wal_tab_transfers' },
  { id: 'zufluss',      labelKey: 'wal_tab_inflows' },
  { id: 'fiat',         labelKey: 'wal_tab_fiat' },
  { id: 'futures',      labelKey: 'wal_tab_futures' },
  { id: 'sonstige',     labelKey: 'wal_tab_other' },
];


// ── Delete Confirm Modal ──────────────────────────────────────────────────────

function DeleteConfirmModal({ count, filterLabel, mode, scope, onConfirm, onCancel, isDeleting }) {
  const { T } = useTheme();
  const { isWide } = useBreakpoint();
  const { t } = useT();
  // mode = 'single' | 'bulk' (default). Single-Variante kommt vom Per-Row-Trash
  // im Desktop-Table und braucht andere Texte als die gefilterte Massenlöschung.
  const isSingle = mode === 'single';
  const strong = (v) => <span style={{ color: T.text, fontWeight: 600 }}>{v}</span>;
  // scope = 'selection' | 'all' | 'filtered' (nur bulk). Steuert den Beschreibungstext.
  const bulkDesc = scope === 'selection'
    ? <>{t('wal_delete_desc_selection_pre')}{strong(count)}{t('wal_delete_desc_selection_post')}</>
    : scope === 'all'
      ? <>{t('wal_delete_desc_all_pre')}{strong(t('wal_delete_desc_all_count', { count }))}{t('wal_delete_desc_all_post')}</>
      : filterLabel
        ? <>{t('wal_delete_desc_filtered_pre')}{strong(`»${filterLabel}«`)}{t('wal_delete_desc_filtered_post')}</>
        : t('wal_delete_desc_filtered_generic');
  return (
    <div
      onClick={() => !isDeleting && onCancel()}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex',
        alignItems: isWide ? 'center' : 'flex-end',
        justifyContent: 'center',
        padding: isWide ? 24 : 0,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 460,
          background: T.surface,
          backdropFilter: 'blur(32px) saturate(180%)',
          WebkitBackdropFilter: 'blur(32px) saturate(180%)',
          borderRadius: isWide ? 3 : '20px 20px 0 0',
          padding: isWide ? '34px 36px 28px' : '30px 24px 40px',
          border: `1px solid ${T.line}`,
          // Editorial-Akzent statt rot getöntem Container: dünner roter
          // Topstrich auf Desktop. Mobile bleibt clean (hat schon top-rounded).
          borderTop: isWide ? `2px solid ${T.red}` : `1px solid ${T.line}`,
          position: 'relative',
        }}
      >
        {/* Eyebrow — sauberes Sans-Caps statt F.mono. Roter Punkt nach "01" als
            Brand-Akzent (Wordmark-Pattern) statt komplett rote UNWIDERRUFLICH-Schrift. */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, marginBottom: 22,
        }}>
          <span style={{
            display: 'inline-flex', alignItems: 'baseline',
            fontFamily: F.family, fontSize: 11, fontWeight: 700,
            letterSpacing: '0.04em', color: T.mute,
          }}>
            <span>01</span>
            <span style={{ color: T.red, marginLeft: 1 }}>.</span>
          </span>
          <span style={{
            fontFamily: F.family, fontSize: 10.5, fontWeight: 700,
            letterSpacing: '0.22em', color: T.text, textTransform: 'uppercase',
          }}>
            {t('wal_irreversible')}
          </span>
          <div style={{ flex: 1, height: 1, background: T.line }} />
        </div>

        {/* Hero — Stroke-only Icon, kein Container, kein Glow */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 18,
          marginBottom: isWide ? 22 : 18,
        }}>
          <svg
            width="40" height="40" viewBox="0 0 20 20"
            fill="none" stroke={T.red} strokeWidth="1.4"
            strokeLinecap="round" strokeLinejoin="round"
            style={{ flexShrink: 0, marginTop: 2 }}
          >
            <path d="M3.5 5.5h13" />
            <path d="M8 3.5h4a1 1 0 0 1 1 1v1H7v-1a1 1 0 0 1 1-1z" />
            <path d="M5 5.5l.9 11a1.2 1.2 0 0 0 1.2 1.1h5.8a1.2 1.2 0 0 0 1.2-1.1l.9-11" />
            <path d="M8.5 8.5v6M11.5 8.5v6" opacity="0.45" />
          </svg>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: F.display,
              fontSize: isWide ? 28 : 24, fontWeight: 700,
              letterSpacing: '-0.035em', lineHeight: 1.05,
              color: T.text, marginBottom: 10,
            }}>
              {isSingle
                ? t('wal_delete_single_title')
                : t(count === 1 ? 'wal_delete_bulk_title_one' : 'wal_delete_bulk_title_many', { count })}
            </div>
            <div style={{
              fontSize: 13, color: T.sub, fontFamily: F.family,
              lineHeight: 1.5, letterSpacing: '-0.005em',
            }}>
              {isSingle
                ? t('wal_delete_single_desc')
                : bulkDesc}
            </div>
          </div>
        </div>

        {/* Trenner über dem Action-Bereich */}
        <div style={{ height: 1, background: T.line, marginBottom: 14 }} />

        {/* Action-Reihe — zwei flache Buttons nebeneinander, keine Schatten,
            kein gefüllter Container. Primary = roter Schrift-Akzent. */}
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            disabled={isDeleting}
            onClick={onCancel}
            style={{
              flex: 1, padding: '13px 14px',
              background: 'transparent', color: T.sub,
              border: `1px solid ${T.line}`, borderRadius: mr(16),
              fontFamily: F.family, fontSize: 13, fontWeight: 600,
              letterSpacing: '-0.005em',
              cursor: isDeleting ? 'not-allowed' : 'pointer',
              transition: `color ${DUR.fast}ms ease, border-color ${DUR.fast}ms ease`,
              WebkitTapHighlightColor: 'transparent',
            }}
            onMouseEnter={e => {
              if (isDeleting) return;
              e.currentTarget.style.color = T.text;
            }}
            onMouseLeave={e => {
              if (isDeleting) return;
              e.currentTarget.style.color = T.sub;
            }}
          >
            {t('cancel')}
          </button>
          <button
            disabled={isDeleting}
            onClick={onConfirm}
            style={{
              flex: 1, padding: '13px 14px',
              background: 'transparent', color: T.red,
              border: `1px solid ${T.line}`, borderRadius: mr(16),
              fontFamily: F.family, fontSize: 13, fontWeight: 700,
              letterSpacing: '-0.005em',
              cursor: isDeleting ? 'not-allowed' : 'pointer',
              opacity: isDeleting ? 0.55 : 1,
              transition: `border-color ${DUR.fast}ms ease, color ${DUR.fast}ms ease`,
              WebkitTapHighlightColor: 'transparent',
            }}
            onMouseEnter={e => {
              if (isDeleting) return;
              e.currentTarget.style.borderColor = T.text;
            }}
            onMouseLeave={e => {
              if (isDeleting) return;
              e.currentTarget.style.borderColor = T.line;
            }}
          >
            {isDeleting ? t('wal_deleting') : t('wal_delete_final')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Hauptkomponente ───────────────────────────────────────────────────────────

// ── CircleClose — runder X-Button, gleicher Kreis-Stil wie BackButton ──
function CircleClose({ T, onClick, title }) {
  const [hover, setHover] = useState(false);
  const { pressTransform, pressHandlers } = usePress({ scale: 0.92 });
  const { t } = useT();
  const label = title || t('wal_close_filter');
  return (
    <button
      className="mo-close"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      {...pressHandlers}
      aria-label={label}
      title={label}
      style={{
        width: 28, height: 28,
        borderRadius: '50%',
        background: hover ? T.itemHover : (T.surfaceHi || 'transparent'),
        border: `1px solid ${hover ? T.text : T.line}`,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer',
        color: hover ? T.text : T.sub,
        flexShrink: 0, padding: 0,
        WebkitTapHighlightColor: 'transparent',
        transform: pressTransform,
        transition: `background ${DUR.fast}ms ease, color ${DUR.fast}ms ease, border-color ${DUR.fast}ms ease, transform ${DUR.press}ms ${EASE}`,
      }}
    >
      <svg className="mo-close-ico" width="12" height="12" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.4"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M6 6l12 12M18 6L6 18" />
      </svg>
    </button>
  );
}

// ── WalletDesktopHeader — exakt nach source/red-screens-wallet.jsx ──────────
// SectionHead (chapter "02", eyebrow "Lückenlose Historie", title "Transaktionen.")
// + Summary-Strip mit 4 Cards (rw-cols-4, gap 16):
//   1. Transaktionen-Count + "geladen"
//   2. Börsen & Wallets count
//   3. On-chain verifiziert (T.gain)
//   4. Quellen-Chips (API · CSV · KI · Manuell)
// editorialType(0.5): pageTitle = 49, pageTitleLs = -0.046
function WalletDesktopHeader({ T, effectiveTxs, activeTab, onBack, unmatchedCount = 0, unmatchedActive = false, onOpenUnmatched, onCloseUnmatched, onOpenDataCheck }) {
  const { t } = useT();
  const RED_C = '#E4544A';
  const byExchange = useMemo(() => {
    const m = {};
    effectiveTxs.forEach(t => {
      const ex = t.exchange || '—';
      m[ex] = (m[ex] || 0) + 1;
    });
    return Object.entries(m);
  }, [effectiveTxs]);

  // Quellen-Breakdown: {label, count} pro Source, sortiert nach Count desc
  const sourceBreakdown = useMemo(() => {
    const counts = {};
    effectiveTxs.forEach(t => {
      const s = t.source || t.importSource;
      let label;
      if (s === 'exchange_api' || s === 'api') label = 'API';
      else if (s === 'csv_import' || s === 'csv') label = 'CSV';
      else if (s === 'ai' || s === 'ki' || s === 'ai_import') label = 'KI';
      else label = 'Manuell';
      counts[label] = (counts[label] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count);
  }, [effectiveTxs]);
  const sources = sourceBreakdown.map(s => s.label);
  // Anzeige-Label je Quelle (API/CSV bleiben sprachneutral, KI/Manuell übersetzt)
  const srcLabel = (label) =>
    label === 'KI' ? t('wal_source_ai')
    : label === 'Manuell' ? t('manual')
    : label;

  const hasOpen = unmatchedCount > 0;
  const lampColor = hasOpen ? (T.red || RED_C) : (T.gain || T.green);

  return (
    <>
      {/* Titel steht im App-Header (DesktopShell h1) — hier keine Duplikat-Überschrift. */}

      {/* Übersichts-Leiste — eigenständige Surface-Card mit dezenten Trennern.
          (Titel steht im App-Header, daher trägt diese Leiste den Seitenkopf.) */}
      <div style={{
        display: 'flex', alignItems: 'stretch', flexWrap: 'wrap',
        marginBottom: 24,
        // Navbar-Ton statt reinweiß (T.surface stach im Tagmodus): warmes Creme.
        background: T.isDark ? T.surface : T.surface2,
        border: `1px solid ${T.line}`,
        borderRadius: 16, overflow: 'hidden',
        boxShadow: `${T.cardInset}, ${T.cardShadow}`,
      }}>
        {[
          <Metric key="tx" T={T} label={t('transactions')} value={effectiveTxs.length} />,
          <Metric key="ex" T={T} label={t('wal_exchanges_wallets')} value={byExchange.length} />,
          // TX Check — öffnet DataCheckSheet. Immer klickbar.
          <motion.div key="open"
            whileTap={{ scale: 0.95 }}
            onClick={onOpenDataCheck}
            style={{ cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: lampColor, flexShrink: 0 }} />
              <span style={{
                fontFamily: F.num || F.mono, fontVariantNumeric: 'tabular-nums',
                fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em',
                color: hasOpen ? T.text : lampColor,
              }}>{unmatchedCount}</span>
            </div>
            <div style={{ fontFamily: F.family, fontSize: 12, fontWeight: 500, color: T.sub, marginTop: 4 }}>
              TX Check
            </div>
          </motion.div>,
          <Metric key="src" T={T} label={t('wal_sources')}
            value={sourceBreakdown.length || 0}
            hint={sources.length ? sourceBreakdown.map(s => srcLabel(s.label)).join(' · ') : null} />,
        ].map((cell, i) => (
          <div key={i} style={{
            flex: '1 1 0', minWidth: 160, padding: '20px 26px',
            borderLeft: i === 0 ? 'none' : `1px solid ${T.lineSoft || T.line}`,
            textAlign: 'center',
          }}>
            {cell}
          </div>
        ))}
      </div>
    </>
  );
}

// Flache Kennzahl: Zahl (Num-Font) über kleinem Label — keine Box.
function Metric({ T, label, value, hint }) {
  return (
    <div>
      <div style={{
        fontFamily: F.num || F.mono, fontVariantNumeric: 'tabular-nums',
        fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', color: T.text,
      }}>{value}</div>
      <div style={{
        fontFamily: F.family, fontSize: 12, fontWeight: 500, color: T.sub, marginTop: 4,
      }}>{label}</div>
      {hint && (
        <div style={{ fontFamily: F.family, fontSize: 11, color: T.mute, marginTop: 2 }}>{hint}</div>
      )}
    </div>
  );
}

// ── Desktop-Steuerleiste für den Einzeln-Löschen-Modus ────────────────────────
// Pendant zum mobilen FAB (der mobile-only ist). Editorial-Stil: rahmenlose
// Ghost-Text-Buttons, kein Panel/Pill, „Löschen" trägt nur roten Text/Stroke.
function DesktopDeleteBar({ T, count, onDelete, onReset, onExit }) {
  const { t } = useT();
  const accent = T.signal || T.red || '#E4544A';
  const btn = (extra) => ({
    display: 'inline-flex', alignItems: 'center', gap: 7,
    padding: '6px 0', background: 'transparent', border: 'none',
    fontFamily: F.family, fontSize: 11, fontWeight: 700,
    letterSpacing: '0.06em', textTransform: 'uppercase',
    cursor: 'pointer', transition: `color ${DUR.fast}ms ease, opacity ${DUR.fast}ms ease`,
    WebkitTapHighlightColor: 'transparent', ...extra,
  });
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
      gap: 18, marginBottom: 14,
    }}>
      <span style={{
        flex: 1, fontFamily: F.num || F.mono, fontSize: 10.5, fontWeight: 600,
        letterSpacing: '0.10em', textTransform: 'uppercase', color: T.sub,
      }}>
        {count === 0 ? t('wal_click_cards') : t('wal_n_selected', { count })}
      </span>
      {count > 0 && (
        <button
          onClick={onReset}
          onMouseEnter={e => { e.currentTarget.style.color = T.text; }}
          onMouseLeave={e => { e.currentTarget.style.color = T.sub; }}
          style={btn({ color: T.sub })}
        >
          {t('wal_reset_selection')}
        </button>
      )}
      <button
        onClick={onExit}
        onMouseEnter={e => { e.currentTarget.style.color = T.text; }}
        onMouseLeave={e => { e.currentTarget.style.color = T.sub; }}
        style={btn({ color: T.sub })}
      >
        <Icon d={Icons.close} size={13} stroke="currentColor" sw={2.4} />
        {t('wal_exit_mode')}
      </button>
      <button
        onClick={onDelete}
        disabled={count === 0}
        onMouseEnter={e => { if (count > 0) e.currentTarget.style.opacity = 0.7; }}
        onMouseLeave={e => { e.currentTarget.style.opacity = 1; }}
        style={btn({
          color: accent,
          cursor: count === 0 ? 'not-allowed' : 'pointer',
          opacity: count === 0 ? 0.4 : 1,
        })}
      >
        <Icon d={Icons.trash} size={14} stroke={accent} sw={2.4} />
        {count > 0 ? t('wal_delete_count', { count }) : t('delete')}
      </button>
    </div>
  );
}

export default function WalletScreen({
  txs, marketData, onEdit, onDelete, onDeleteBulk, onLinkTransfer, onUnlinkTransfer, patchTx,
  viewState, onViewStateChange, onBack, onOpenResolve,
}) {
  const { T } = useTheme();
  const { isWide } = useBreakpoint();
  const { t } = useT();
  // View-State aus App.js (überlebt Edit-Roundtrips). Fallback auf lokale Defaults.
  const vs = viewState || { filterText: '', activeTab: 'all', filterUnmatched: false };
  const updateVs = onViewStateChange || (() => {});
  const filterText      = vs.filterText;
  const activeTab       = vs.activeTab;
  const filterUnmatched = vs.filterUnmatched;
  const setFilterText      = (v) => updateVs({ filterText: typeof v === 'function' ? v(filterText) : v });
  const setActiveTab       = (v) => updateVs({ activeTab: typeof v === 'function' ? v(activeTab) : v });
  const setFilterUnmatched = (v) => updateVs({ filterUnmatched: typeof v === 'function' ? v(filterUnmatched) : v });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting,        setIsDeleting]        = useState(false);
  const [showLinkSheet,     setShowLinkSheet]     = useState(false);
  // Einzeln-Löschen-Modus (Karten anhaken, dann gesammelt löschen)
  const [deleteMode,        setDeleteMode]        = useState(false);
  const [deleteSelectedIds, setDeleteSelectedIds] = useState(new Set());
  const clearFilterPress = usePress({ scale: 0.92 });
  const toggleDeleteSelect = (id) => {
    setDeleteSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const exitDeleteMode = () => { setDeleteMode(false); setDeleteSelectedIds(new Set()); };
  // Manuelles Transfer-Verbinden
  const {
    effectiveTxs: linkedTxs, linkMode, setLinkMode,
    selectedIds, selectedCount, toggleSelect, clearSelection,
    candidate, suggestionMap, isLinking, confirmLink, unlink,
  } = useTransferLinking({ txs, onLink: onLinkTransfer, onUnlink: onUnlinkTransfer });

  // Timeline Reorder (komponiert über linkedTxs)
  const {
    effectiveTxs, reorderMode, setReorderMode,
    commitReorder,
  } = useTimelineReorder({ txs: linkedTxs, patchTx: patchTx || (() => Promise.resolve()) });

  // Ob irgendeine TX einen SUSPICIOUS_TIMESTAMP-Warning hat
  const hasSuspiciousTimestamps = useMemo(
    () => effectiveTxs.some(tx => tx._issues?.some(i => i.code === 'SUSPICIOUS_TIMESTAMP')),
    [effectiveTxs]
  );

  // Transfer-Map für alle Transaktionen (optimistische Sicht)
  const transferMap = useMemo(() => buildTransferMap(effectiveTxs), [effectiveTxs]);

  // Historische EUR-Werte für externe Transfers ohne erfassten Preis.
  // Display ruft convert() drauf, daher fest in EUR laden.
  const derivedValues = useHistoricalPrices(effectiveTxs, { currency: 'eur' });

  // Scam-Dust-IDs: Read-Layer-Kandidat (_scamDust) + WERT-Veto (aktueller Preis).
  // Zentral berechnet, damit Filter, Zaehlung und Tab-Sichtbarkeit dieselbe
  // wert-basierte Entscheidung nutzen wie der Badge in buildTxView.
  const scamDustIds = useMemo(() => {
    const s = new Set();
    for (const tx of effectiveTxs) {
      if (!tx._scamDust) continue;
      const id = tx._docId || String(tx.id);
      // Obskure Scam-Coins haben oft keinen Marktpreis → Tageswert (derivedValues)
      // nachziehen, sonst rutschen 0,001-ONDO-artige Mikro-Eingänge durch.
      const v = txValueFromMarket(tx, marketData) || Number(derivedValues?.get?.(id)) || 0;
      if (resolveScamDust(tx, v)) s.add(id);
    }
    return s;
  }, [effectiveTxs, marketData, derivedValues]);
  const isDust = (tx) => scamDustIds.has(tx._docId || String(tx.id));

  // Lookup-Map: txId → partner-tx
  const linkedTxMap = useMemo(() => {
    const map = new Map();
    for (const tx of effectiveTxs) {
      const id = tx._docId || String(tx.id);
      const linkedId = tx.linkedTxId || transferMap.get(id)?.linkedTxId;
      if (linkedId) {
        const partner = effectiveTxs.find(t => (t._docId || String(t.id)) === linkedId);
        if (partner) map.set(id, partner);
      }
    }
    return map;
  }, [effectiveTxs, transferMap]);

  // TX Check — DataCheck-Objekt für Sheet + Zähler für das Lämpchen.
  const [dcOpen, setDcOpen] = useState(false);
  const dataCheck = useMemo(
    () => buildDataCheck({ txs: effectiveTxs, marketData }),
    [effectiveTxs, marketData]
  );
  const unmatchedCount = dataCheck.openCount;

  // Gefilterte und sortierte Transaktionen
  const filteredTxs = useMemo(() => {
    // Bei aktiver Textsuche zeigen wir auch sonst ausgeblendete TXs
    // (typ=gebuehr, Fiat-Transfers, NFT-Bewegungen). Sonst sieht der User die
    // 44 „Geister-TXs" nicht, die sein Suchwort matchen — und löscht blind.
    const isSearching = !!filterText;
    const isHiddenTab = activeTab === 'sonstige';
    const isFiatTab   = activeTab === 'fiat';
    const base = [...effectiveTxs]
      .filter(tx => {
        // Auch verdichtete TXs ohne reale Menge ausblenden (defensive)
        if (tx._mergedHidden) return false;

        if (isHiddenTab) {
          // Sonstige-Tab: technische Einträge (interne Gebühren, NFT) + Scam-Dust.
          // Überspringt den normalen Typ-Filter.
          if (!isHiddenTx(tx) && !isDust(tx)) return false;
        } else if (isFiatTab) {
          // Fiat-Tab „Ein-/Auszahlung €": NUR Fiat-Geldflüsse (EUR/USD …).
          if (!isFiatTransfer(tx)) return false;
        } else if (!isSearching) {
          // Standardliste/Krypto-Tabs: NFT-Bewegungen und interne Trading-Fees
          // ausblenden (FIFO verarbeitet sie automatisch). Fiat bleibt im
          // „Alle"-Tab sichtbar, wird aber aus den Krypto-Einzahlung/-Auszahlung-
          // Tabs ausgeblendet (eigene „Ein-/Auszahlung €"-Kategorie).
          if (isNftTransfer(tx)) return false;
          if (isInternalFeeTx(tx)) return false;
          if (isDust(tx)) return false;
        }

        // Offene-Transfers-Filter überschreibt alle anderen — gleiche Quelle
        // (isOpenTransfer) wie die Zählung und der Datencheck.
        if (filterUnmatched) {
          return isOpenTransfer(tx, transferMap, isDust);
        }

        // Kategorie-Filter (Alle / Kauf / Verkauf / Tausch / Transfers / Zuflüsse
        // / Futures) — für Sonstige- UND Fiat-Tab übersprungen (eigene Erkennung).
        if (!isHiddenTab && !isFiatTab && activeTab !== 'all' &&
            txDisplayCategory(tx, transferMap) !== activeTab) return false;

        // Text-Suche
        if (filterText) {
          const q      = filterText.toLowerCase();
          const coin   = (tx.coin || tx.coinOut || '').toLowerCase();
          const coinIn = (tx.coinIn || '').toLowerCase();
          // exchange UND platform durchsuchen — alte AI/Generic-Importe haben
          // die Börse oft in platform statt exchange abgelegt. contentHash
          // nutzt den OR-Fallback auch (txFingerprint.contentHash), also muss
          // die Suche das spiegeln, sonst sind die TXs „unsichtbar löschbar".
          const exch   = (tx.exchange || tx.platform || '').toLowerCase();
          // wallet MIT durchsuchen: über Dienstleister gekaufte Coins (z.B. MoonPay)
          // tragen die Ziel-Wallet (Phantom/Ledger) in tx.wallet, nicht in exchange.
          // Ohne das findet die Suche nach „phantom" diese Käufe nicht, obwohl die
          // Coins physisch dort liegen.
          const wlt    = (tx.wallet || '').toLowerCase();
          const notiz  = (tx.notiz || '').toLowerCase();
          const datum  = (tx.datum || '').toLowerCase();
          const typ    = getTxLabel(tx.typ).toLowerCase();
          if (
            !coin.includes(q) && !coinIn.includes(q) &&
            !exch.includes(q) && !wlt.includes(q) && !notiz.includes(q) &&
            !datum.includes(q) && !typ.includes(q)
          ) return false;
        }

        return true;
      });

    // Bei aktiver Textsuche kann ein Transfer-Bein matchen, sein verknüpftes
    // Gegen-Bein aber nicht (Suche „Phantom": die Phantom-Auszahlung matcht, die
    // Ledger-Einzahlung trägt »Ledger« und fällt raus). Ohne das Gegen-Bein
    // bildet buildGroupedChains kein Paar → die Zeile fällt auf die Einzel-Karte
    // „gesendet" zurück statt der Transfer-Karte „transferiert" (Parität zum
    // Coin-Detail gebrochen, wirkt wie ein Versand). Darum ziehen matchende
    // Transfer-Beine ihr verknüpftes Gegen-Bein nach.
    let withPartners = base;
    if (isSearching) {
      const present = new Set(base.map(t => t._docId || String(t.id)));
      const extra = [];
      for (const tx of base) {
        const partner = linkedTxMap.get(tx._docId || String(tx.id));
        if (!partner) continue;
        const pid = partner._docId || String(partner.id);
        if (present.has(pid) || partner._mergedHidden) continue;
        extra.push(partner);
        present.add(pid);
      }
      if (extra.length) withPartners = base.concat(extra);
    }

    return withPartners
      .sort((a, b) => {
        // effectiveTimestamp überschreibt datum+zeit (manuelles Reordering)
        const da = a.effectiveTimestamp?.slice(0, 10) || a.datum || '';
        const db = b.effectiveTimestamp?.slice(0, 10) || b.datum || '';
        if (da !== db) return db.localeCompare(da); // DESC für Display
        const ta = a.effectiveTimestamp ? a.effectiveTimestamp.slice(11, 19) : (a.zeit || '00:00:00');
        const tb = b.effectiveTimestamp ? b.effectiveTimestamp.slice(11, 19) : (b.zeit || '00:00:00');
        if (ta !== tb) return tb.localeCompare(ta);
        // manualOrderIndex als Tiebreaker
        const oa = a.manualOrderIndex ?? Infinity, ob = b.manualOrderIndex ?? Infinity;
        if (oa !== ob) return oa - ob;
        return String(b._docId || b.id).localeCompare(String(a._docId || a.id));
      });
  }, [effectiveTxs, filterText, activeTab, filterUnmatched, transferMap, scamDustIds, linkedTxMap]);

  // Nach Filter + Sort die TXs verdichten (Binance-Sekunden-Splits → eine Karte).
  // Im linkMode/reorderMode/deleteMode KEINE Verdichtung — User muss die
  // einzelnen TXs sehen können um sie zu manipulieren.
  const displayTxs = useMemo(() => {
    if (linkMode || reorderMode || deleteMode) return filteredTxs;
    return mergeTxsByDay(filteredTxs);
  }, [filteredTxs, linkMode, reorderMode, deleteMode]);

  // Delete-Ziel: WEITERE Auswahl als filteredTxs. Erfasst auch versteckte
  // TXs (typ=gebuehr, Fiat-Transfers, NFT-Bewegungen), wenn sie den Text-
  // Filter matchen. Sonst bleiben alte gebuehr-TXs (vom alten Parser oder
  // BNB-Fees auf Non-BNB-Trades) als unsichtbare Phantom-Hashes liegen und
  // erscheinen beim nächsten Import als Geister-Konflikte.
  // „Alle löschen" (kein Filter) erfasst BEWUSST auch die ausgeblendeten
  // EUR-Withdraws/NFT/Gebühren — der User erwartet, dass „alle 448" wirklich
  // alle sind. Die versteckten Einträge sind über den Sonstige-Tab einsehbar.
  const deleteTargetTxs = useMemo(() => {
    // „Alle löschen" (kein Filter, Tab = Alle): WIRKLICH alles — inkl. der
    // normalerweise ausgeblendeten technischen Einträge (Gebühren, Fiat,
    // NFT). Sonst bleiben die ~113 unsichtbaren Einträge liegen, die der
    // User nicht sieht und die beim nächsten Import als Konflikt-Hashes
    // zurückkommen. (_mergedHidden = reine Anzeige-Dubletten, raus.)
    if (!filterText && activeTab === 'all' && !filterUnmatched) {
      return effectiveTxs.filter(tx => !tx._mergedHidden);
    }
    const q = filterText ? filterText.toLowerCase() : null;
    const isHiddenTab = activeTab === 'sonstige';
    const isFiatTab   = activeTab === 'fiat';
    return effectiveTxs.filter(tx => {
      if (tx._mergedHidden) return false;
      // Sonstige-/Fiat-Tab: nur die jeweilige Teilmenge; sonst Typ-Filter respektieren.
      if (isHiddenTab) {
        if (!isHiddenTx(tx) && !isDust(tx)) return false;
      } else if (isFiatTab) {
        if (!isFiatTransfer(tx)) return false;
      } else if (activeTab !== 'all' && txDisplayCategory(tx, transferMap) !== activeTab) return false;
      // Suche matcht über alle Felder — gebuehr/Fiat/NFT explizit MITgeprüft.
      // platform muss mit rein (siehe Filter oben), sonst werden „Geister-TXs"
      // mit exchange="" / platform="Binance" nicht erfasst und matchen beim
      // nächsten Import wieder als Konflikt.
      if (q) {
        const coin   = (tx.coin || tx.coinOut || '').toLowerCase();
        const coinIn = (tx.coinIn || '').toLowerCase();
        const exch   = (tx.exchange || tx.platform || '').toLowerCase();
        const notiz  = (tx.notiz || '').toLowerCase();
        const datum  = (tx.datum || '').toLowerCase();
        const typ    = getTxLabel(tx.typ).toLowerCase();
        if (
          !coin.includes(q) && !coinIn.includes(q) &&
          !exch.includes(q) && !notiz.includes(q) &&
          !datum.includes(q) && !typ.includes(q)
        ) return false;
      }
      return true;
    });
  }, [effectiveTxs, filterText, activeTab, filterUnmatched, transferMap, scamDustIds]);

  const grouped = useMemo(() => buildGroupedChains(displayTxs, transferMap), [displayTxs, transferMap]);

  // Verbinden möglich? (genau 2 gewählt, keine harten Blocker)
  const canLink = selectedCount === 2 && candidate && candidate.blockers.length === 0;
  const blockerMsg = selectedCount === 2 && candidate?.blockers.length
    ? candidate.blockers[0].message
    : null;

  if (effectiveTxs.length === 0) {
    return (
      <div style={{ padding: isWide ? '60px 20px' : '40px 20px 100px', textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
        <div style={{
          fontSize: 16, fontWeight: 700, color: T.text,
          fontFamily: F.family, marginBottom: 6,
        }}>{t('wal_empty_title')}</div>
        <div style={{ fontSize: 13, color: T.muted, fontFamily: F.family }}>
          {t('wal_empty_desc')}
        </div>
      </div>
    );
  }

  // Punktmenü-Aktionen (Desktop) — volle Parität zum mobilen FAB-Normal-Modus.
  const kebabActions = (() => {
    const list = [];
    if (unmatchedCount > 0 || hasSuspiciousTimestamps) {
      list.push({
        key: 'sort', label: t('wal_sort'), icon: Icons.swap,
        onClick: () => { setLinkMode(false); setReorderMode(true); },
      });
    }
    list.push({
      key: 'pickdel', label: t('wal_delete_individually'), icon: Icons.trash,
      onClick: () => { setLinkMode(false); setReorderMode(false); setDeleteMode(true); },
    });
    list.push({
      key: 'delete', danger: true, icon: Icons.trash,
      label: filterText ? t('wal_delete_filtered') : t('wal_delete_all'),
      disabled: deleteTargetTxs.length === 0,
      onClick: () => deleteTargetTxs.length > 0 && setShowDeleteConfirm(true),
    });
    return list;
  })();

  return (
    // Calm: ruhige, zentrierte Lese-Spalte auf Desktop (wie der Mock), damit der
    // Verlauf nicht über die volle Shell-Breite zerfließt.
    <div style={{
      padding: isWide ? '0 0 48px' : '8px 18px 180px',
      maxWidth: isWide ? 880 : undefined,
      margin: isWide ? '0 auto' : undefined,
    }}>

      {/* ── Desktop Header: Titel allein + leise Kennzahlen-Zeile (flach) ── */}
      {isWide ? (
        <WalletDesktopHeader
          T={T}
          effectiveTxs={effectiveTxs}
          activeTab={activeTab}
          onBack={onBack}
          unmatchedCount={unmatchedCount}
          unmatchedActive={filterUnmatched}
          onOpenUnmatched={() => (onOpenResolve ? onOpenResolve() : setFilterUnmatched(true))}
          onCloseUnmatched={() => setFilterUnmatched(false)}
          onOpenDataCheck={() => setDcOpen(true)}
        />
      ) : (
        <div style={{ padding: '4px 0 14px' }}>
          <h1 style={{
            fontFamily: F.display, fontSize: 30, fontWeight: 700,
            letterSpacing: '-0.04em', margin: 0, color: T.text,
          }}>{t('transactions')}</h1>
          <div style={{ fontSize: 13.5, color: T.sub, marginTop: 4, fontFamily: F.family }}>
            {t('wal_sub_calm')}
          </div>
        </div>
      )}

      {/* ── Calm-Suche: weiche erhabene Fläche (kein versenktes Well) ──────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '11px 14px', borderRadius: 13, marginBottom: 12,
        background: T.surfaceHi,
        border: `1px solid ${T.line}`,
      }}>
        <svg width={15} height={15} viewBox="0 0 24 24" fill="none"
          stroke={T.sub} strokeWidth={2} strokeLinecap="round">
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
        </svg>
        <input
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
          placeholder={t('wal_search_placeholder')}
          style={{
            flex: 1, background: 'transparent', border: 'none', outline: 'none',
            fontSize: 13.5, fontWeight: 500, color: T.text, fontFamily: F.family,
            letterSpacing: '-0.01em', minWidth: 0,
          }}
        />
        {filterText && (
          <button onClick={() => setFilterText('')} {...clearFilterPress.pressHandlers} style={{
            width: 22, height: 22, borderRadius: 11, padding: 0,
            background: T.isDark ? 'rgba(255,255,255,0.10)' : 'rgba(12,12,15,0.08)',
            border: 'none', cursor: 'pointer', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: T.sub, fontSize: 14, lineHeight: 1,
            transform: clearFilterPress.pressTransform,
            transition: `transform ${DUR.press}ms ${EASE}`,
          }}>×</button>
        )}
        {/* Punktmenü direkt in der Suchleiste (Desktop) — spart die separate
            Zeile darunter und damit den vertikalen Leerraum. Im deleteMode
            übernimmt die DesktopDeleteBar unter der Leiste. */}
        {isWide && !deleteMode && (
          <>
            <div style={{ width: 1, height: 20, background: T.line, flexShrink: 0 }} />
            <KebabMenu align="right" title={t('wal_actions')} buttonSize={30} actions={kebabActions} />
          </>
        )}
      </div>

      {/* ── deleteMode-Steuerleiste (Desktop) — Pendant zum mobile-only FAB.
           Erscheint NUR während aktiver Einzel-Auswahl, daher kein Dauer-Gap.
           Das Punktmenü selbst sitzt im Normalfall in der Suchleiste oben. */}
      {isWide && deleteMode && (
        <DesktopDeleteBar
          T={T}
          count={deleteSelectedIds.size}
          onDelete={() => deleteSelectedIds.size > 0 && setShowDeleteConfirm(true)}
          onReset={() => setDeleteSelectedIds(new Set())}
          onExit={exitDeleteMode}
        />
      )}

      {/* ── Offene Transfers Hinweis (dezent, design-konform) ─────────────────
           Nur Mobile: auf Desktop ersetzt die klickbare Header-Kachel diesen Banner. */}
      {!isWide && unmatchedCount > 0 && !filterUnmatched && (
        <div
          className="mo-row"
          onClick={() => setDcOpen(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 14px', borderRadius: 13, marginBottom: 14,
            background: T.isDark ? 'rgba(255,255,255,0.05)' : 'rgba(12,12,15,0.04)',
            boxShadow: T.isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : 'inset 0 1px 0 rgba(255,255,255,0.6)',
            cursor: 'pointer',
          }}
        >
          <div style={{
            width: 6, height: 6, borderRadius: '50%', background: T.red, flexShrink: 0,
          }} />
          <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{
              fontSize: 13, fontWeight: 600, color: T.text, fontFamily: F.family, letterSpacing: '-0.01em',
            }}>
              {unmatchedCount} {unmatchedCount === 1 ? t('wal_open_transfer_one') : t('wal_open_transfer_many')}
            </span>
            <span style={{ fontSize: 12, color: T.sub, fontFamily: F.family }}>
              · {t('wal_review_now')}
            </span>
          </div>
          <svg className="mo-chev" width={12} height={12} viewBox="0 0 24 24" fill="none"
            stroke={T.sub} strokeWidth={2.2} strokeLinecap="round">
            <path d="M9 18l6-6-6-6"/>
          </svg>
        </div>
      )}

      {/* Aktiver Offene-Transfers-Filter — nur Mobile.
           Auf Desktop steuert die Header-Kachel den Filter (Klick = Toggle). */}
      {!isWide && filterUnmatched && !linkMode && (
        <div style={{ marginBottom: 14 }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 14px', borderRadius: 13,
            background: T.isDark ? 'rgba(255,255,255,0.05)' : 'rgba(12,12,15,0.04)',
            boxShadow: T.isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : 'inset 0 1px 0 rgba(255,255,255,0.6)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: T.red, flexShrink: 0 }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: T.text, fontFamily: F.family, letterSpacing: '-0.01em' }}>
                {t('wal_open_transfers')}
              </span>
              <span style={{ fontSize: 12, color: T.sub, fontFamily: F.family }}>
                · {filteredTxs.length}
              </span>
            </div>
            <button
              className="mo-close"
              onClick={() => setFilterUnmatched(false)}
              aria-label={t('wal_close_filter')}
              style={{
                width: 26, height: 26, borderRadius: 8, padding: 0,
                background: 'transparent', border: `1px solid ${T.line}`,
                cursor: 'pointer', color: T.sub, flexShrink: 0,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <svg className="mo-close-ico" width={11} height={11} viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth={2.4} strokeLinecap="round">
                <path d="M6 6l12 12M18 6L6 18"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Verbinden-Modus Header */}
      {linkMode && (
        <div style={{
          padding: '10px 14px', borderRadius: 13, marginBottom: 14,
          background: T.isDark ? 'rgba(255,255,255,0.05)' : 'rgba(12,12,15,0.04)',
          boxShadow: T.isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : 'inset 0 1px 0 rgba(255,255,255,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
        }}>
          <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: T.red, flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.text, fontFamily: F.family, letterSpacing: '-0.01em' }}>
                {t('wal_link_transfers')}
              </div>
              <div style={{ fontSize: 11, color: T.sub, fontFamily: F.family, marginTop: 1 }}>
                {selectedCount === 0 && t('wal_link_hint_first')}
                {selectedCount === 1 && t('wal_link_hint_second')}
                {selectedCount === 2 && t('wal_link_hint_ready')}
              </div>
            </div>
          </div>
          <button
            onClick={() => setLinkMode(false)}
            style={{
              flexShrink: 0,
              background: 'transparent', border: 'none',
              padding: '4px 6px', cursor: 'pointer',
              fontSize: 12, fontWeight: 500, color: T.sub, fontFamily: F.family,
              letterSpacing: '-0.01em',
            }}
          >{t('cancel')}</button>
        </div>
      )}

      {/* Einzeln-Löschen-Modus Header */}
      {deleteMode && (
        <div style={{
          padding: '10px 14px', borderRadius: 13, marginBottom: 14,
          background: T.surfaceHi,
          border: `1px solid ${T.line}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
        }}>
          <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: T.red, flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.text, fontFamily: F.family, letterSpacing: '-0.01em' }}>
                {t('wal_delete_individually')}
              </div>
              <div style={{ fontSize: 11, color: T.sub, fontFamily: F.family, marginTop: 1 }}>
                {deleteSelectedIds.size === 0
                  ? t('wal_tap_cards')
                  : t('wal_n_selected', { count: deleteSelectedIds.size })}
              </div>
            </div>
          </div>
          <button
            className="mo-close"
            onClick={exitDeleteMode}
            aria-label={t('wal_close_selection_mode')}
            style={{
              width: 26, height: 26, borderRadius: 8, padding: 0,
              background: 'transparent', border: `1px solid ${T.line}`,
              cursor: 'pointer', color: T.sub, flexShrink: 0,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <svg className="mo-close-ico" width={11} height={11} viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth={2.4} strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18"/>
            </svg>
          </button>
        </div>
      )}

      {/* Reorder-Modus — kein Top-Banner mehr, wird durch Floating Bar unten ersetzt */}

      {/* ── Kategorie-Tabs (Segmented-Style, identisch zum Verlaufs-Range) ── */}
      {!filterUnmatched && (() => {
        const visibleOptions = FILTER_TABS
          .filter(tab => {
            if (tab.id === 'all') return true;
            if (tab.id === 'sonstige') return effectiveTxs.some(t => isHiddenTx(t) || isDust(t));
            if (tab.id === 'fiat') return effectiveTxs.some(isFiatTransfer);
            // Kategorie-Tabs: nur zeigen, wenn (nicht versteckte) TXs der Kategorie
            // existieren — sonst kein leerer Tab.
            return effectiveTxs.some(t =>
              !isHiddenTx(t) && !isDust(t) && txDisplayCategory(t, transferMap) === tab.id);
          })
          .map(tab => ({ value: tab.id, label: t(tab.labelKey) }));
        if (visibleOptions.length <= 1) return null;
        return (
          <div style={{
            marginBottom: 16,
            overflowX: 'auto',
            scrollbarWidth: 'none', msOverflowStyle: 'none',
          }}>
            <Segmented
              bare
              value={activeTab}
              onChange={setActiveTab}
              options={visibleOptions}
            />
          </div>
        );
      })()}

      {/* Kein Ergebniszähler-Label mehr (Calm-Regel: keine Count-Captions). */}

      {/* ── Keine Treffer ──────────────────────────────────────────────────── */}
      {filteredTxs.length === 0 && (
        <div style={{
          textAlign: 'center', padding: '48px 20px 40px',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
        }}>
          <img
            src="/red-logo-192.svg"
            alt="red."
            width={56} height={56}
            style={{ borderRadius: 12, opacity: 0.85, marginBottom: 18 }}
          />
          <div style={{
            fontFamily: F.display, fontSize: 18, fontWeight: 700,
            letterSpacing: '-0.02em', color: T.text, marginBottom: 6,
          }}>{t('wal_no_results')}</div>
          <div style={{
            fontFamily: F.family, fontSize: 13,
            color: T.sub || T.muted, maxWidth: 280, lineHeight: 1.5,
          }}>
            {t('wal_no_results_desc')}
          </div>
        </div>
      )}

      {/* ── Tages-Feed (Calm): nach Tagen gruppierter Klartext-Verlauf, auf
           Desktop UND Mobile. Keine Tabelle mehr — Anti-CoinTracking. ────── */}
      {grouped.map(([dateKey, items]) => {
        const singleItems = items.filter(g => g.type === 'single');
        const hasManualOrder = singleItems.some(g => g.txs[0].manualOrderIndex != null);

        return (
          <div key={dateKey} style={{
            marginBottom: 8,
            // Performance: Browser überspringt offscreen-Render bei vielen Gruppen.
            // Echter Effekt ab ~500 TX. containIntrinsicSize ist ein Platzhalter,
            // bis die Gruppe sichtbar wird → braucht den Browser-Layout nicht zu blocken.
            contentVisibility: 'auto',
            containIntrinsicSize: '0 300px',
          }}>
            {/* Datums-Header (Calm: Klartext „Heute/Gestern", keine Caps) */}
            <div style={{
              fontFamily: F.family, fontSize: 13, fontWeight: 700,
              letterSpacing: '-0.01em', color: T.sub,
              padding: '18px 4px 8px',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              {formatDateHeader(dateKey, t)}
              {reorderMode && hasManualOrder && (
                <span style={{ fontSize: 11, fontStyle: 'italic', fontWeight: 400, color: '#8B5CF6' }}>
                  · {t('wal_manually_sorted')}
                </span>
              )}
            </div>

            {/* TX-Karten für diesen Tag */}
            {reorderMode ? (
              /* Reorder-Modus: alle Items (Singles + Chains) draggbar */
              <Reorder.Group
                axis="y"
                values={items}
                onReorder={newOrder => {
                  // Anzeige ist DESC (neueste oben). Der Lot-System-Sort ist ASC
                  // (Frühestes zuerst). Damit die Verarbeitungsreihenfolge der
                  // visuellen Reihenfolge entspricht, muss idx 0 (oben = spätestes
                  // Ereignis) die HÖCHSTE Zeit bekommen → invertieren.
                  const last = newOrder.length - 1;
                  newOrder.forEach((group, idx) => {
                    const inv = last - idx;
                    if (group.type === 'single') {
                      const tx = group.txs[0];
                      const id = tx._docId || String(tx.id);
                      const ts = `${dateKey}T12:00:${String(inv).padStart(2, '0')}.000Z`;
                      if (tx.effectiveTimestamp !== ts || (tx.manualOrderIndex ?? -1) !== idx) {
                        commitReorder(id, ts, idx);
                      }
                    } else if (group.type === 'chain') {
                      // Chain: auszahlung MUSS vor einzahlung im Lot-System laufen
                      // (markLotsForTransfer → completeLotTransfer). Beide bekommen
                      // denselben Sekunden-Slot, aber auszahlung 0ms, einzahlung 500ms.
                      const [txOut, txIn] = group.txs;
                      const tsOut = `${dateKey}T12:00:${String(inv).padStart(2, '0')}.000Z`;
                      const tsIn  = `${dateKey}T12:00:${String(inv).padStart(2, '0')}.500Z`;
                      if (txOut._docId && (txOut.effectiveTimestamp !== tsOut || (txOut.manualOrderIndex ?? -1) !== idx)) {
                        commitReorder(txOut._docId, tsOut, idx);
                      }
                      if (txIn._docId && (txIn.effectiveTimestamp !== tsIn || (txIn.manualOrderIndex ?? -1) !== idx)) {
                        commitReorder(txIn._docId, tsIn, idx);
                      }
                    }
                  });
                }}
                style={{ display: 'flex', flexDirection: 'column', gap: 7, listStyle: 'none', padding: 0, margin: 0 }}
              >
                {items.map((group) => {
                  if (group.type === 'chain') {
                    const [txA, txB] = group.txs;
                    const idA = txA._docId || String(txA.id);
                    return (
                      <ReorderableChainCard
                        key={`chain-${idA}`}
                        group={group}
                        txA={txA}
                        txB={txB}
                        marketData={marketData}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onUnlink={unlink}
                      />
                    );
                  }
                  const tx = group.txs[0];
                  const id = tx._docId || String(tx.id);
                  return (
                    <ReorderableCard
                      key={id}
                      group={group}
                      tx={tx}
                      id={id}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      transferMap={transferMap}
                      marketData={marketData}
                      linkedTx={linkedTxMap.get(id)}
                    />
                  );
                })}
              </Reorder.Group>
            ) : (
              /* Normal-Modus — flacher Calm-Feed (Hairline-Trenner, gap 0).
                 Link-/Delete-Mode nutzen die alten TxCards → brauchen Abstand. */
              <div style={{ display: 'flex', flexDirection: 'column', gap: (linkMode || deleteMode) ? 8 : 0 }}>
                {items.map((group) => {
                  if (group.type === 'single') {
                    const tx = group.txs[0];
                    const id = tx._docId || String(tx.id);
                    if (linkMode) {
                      return (
                        <TxCard
                          key={id}
                          tx={tx}
                          isOpen={false}
                          onToggle={() => {}}
                          onEdit={onEdit}
                          onDelete={onDelete}
                          transferMap={transferMap}
                          marketData={marketData}
                          linkedTx={linkedTxMap.get(id)}
                          selectable
                          selected={selectedIds.has(id)}
                          onSelectToggle={() => toggleSelect(id)}
                          suggestion={suggestionMap.get(id)}
                          dimmed={selectedCount === 1 && !selectedIds.has(id) && !suggestionMap.has(id)}
                        />
                      );
                    }
                    if (deleteMode) {
                      return (
                        <TxCard
                          key={id}
                          tx={tx}
                          isOpen={false}
                          onToggle={() => {}}
                          onEdit={onEdit}
                          onDelete={onDelete}
                          transferMap={transferMap}
                          marketData={marketData}
                          linkedTx={linkedTxMap.get(id)}
                          selectable
                          selectVariant="delete"
                          selected={deleteSelectedIds.has(id)}
                          onSelectToggle={() => toggleDeleteSelect(id)}
                        />
                      );
                    }
                    // Einzelner Krypto-Wallet-Transfer (externer Versand/Empfang
                    // ohne erfasstes Gegenstück) → aufklappbare Transfer-Karte,
                    // optisch identisch zu den gematchten Paaren. Fiat-Ein/Aus-
                    // zahlung + alle übrigen Typen bleiben die normale Zeile.
                    if (isCryptoWalletTransfer(tx, transferMap)) {
                      return (
                        <TransferChainCard
                          key={`xfer-${id}`}
                          txA={tx}
                          txB={null}
                          marketData={marketData}
                          transferMap={transferMap}
                          derivedValues={derivedValues}
                          onEdit={onEdit}
                          onDelete={onDelete}
                          flat
                        />
                      );
                    }
                    return (
                      <WalletRow
                        key={id}
                        tx={tx}
                        marketData={marketData}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        transferMap={transferMap}
                        derivedValues={derivedValues}
                      />
                    );
                  }
                  const [txA, txB] = group.txs;
                  const idA = txA._docId || String(txA.id);
                  return (
                    <TransferChainCard
                      key={`chain-${idA}`}
                      txA={txA}
                      txB={txB}
                      marketData={marketData}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      onUnlink={unlink}
                      flat
                    />
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* ── Delete-Confirm-Modal ───────────────────────────────────────────── */}
      {showDeleteConfirm && (() => {
        const inDeleteMode = deleteMode;
        // Bulk-Delete benutzt deleteTargetTxs (erfasst versteckte gebuehr/Fiat
        // wenn Filter aktiv), nicht filteredTxs. Sonst bleiben Phantom-Hashes
        // beim nächsten Import als Geister-Konflikte hängen.
        const targets = inDeleteMode
          ? effectiveTxs.filter(t => deleteSelectedIds.has(t._docId || String(t.id)))
          : deleteTargetTxs;
        return (
          <DeleteConfirmModal
            count={targets.length}
            filterLabel={inDeleteMode ? null : (filterText || null)}
            scope={
              inDeleteMode ? 'selection'
              : (!filterText && activeTab === 'all' && !filterUnmatched) ? 'all'
              : 'filtered'
            }
            isDeleting={isDeleting}
            onConfirm={async () => {
              setIsDeleting(true);
              try {
                await onDeleteBulk(targets);
                setShowDeleteConfirm(false);
                if (inDeleteMode) exitDeleteMode();
                else setFilterText('');
              } catch (err) {
                console.error('[WalletScreen] Löschen fehlgeschlagen:', err);
              } finally {
                setIsDeleting(false);
              }
            }}
            onCancel={() => setShowDeleteConfirm(false)}
          />
        );
      })()}

      {/* ── Verbinden-Vorschau-Sheet ───────────────────────────────────────── */}
      {showLinkSheet && candidate && (
        <LinkTransferSheet
          candidate={candidate}
          marketData={marketData}
          isLinking={isLinking}
          onCancel={() => setShowLinkSheet(false)}
          onConfirm={async () => {
            await confirmLink();
            setShowLinkSheet(false);
          }}
        />
      )}

      {/* ── Floating Action Button (kontext-sensitiv) ─────────────────────── */}
      {/* Desktop hat die Aktionen schon in Sidebar/Topbar — FAB nur Mobile/Tablet. */}
      {!isWide && (
      <TransactionsFab
        counterLabel={
          reorderMode
            ? t('wal_edit_order')
            : deleteMode
              ? (deleteSelectedIds.size === 0 ? t('wal_tap_cards_short') : t('wal_n_selected', { count: deleteSelectedIds.size }))
              : linkMode
                ? (blockerMsg
                    ? blockerMsg
                    : selectedCount === 2 ? t('wal_link_hint_ready') : t('wal_n_of_2_selected', { count: selectedCount }))
                : filterUnmatched
                  ? t(filteredTxs.length === 1 ? 'wal_n_open_one' : 'wal_n_open_many', { count: filteredTxs.length })
                  : null
        }
        accent={
          (filterUnmatched && filteredTxs.length >= 2 && !linkMode && !reorderMode && !deleteMode) ||
          (linkMode && canLink) ||
          (deleteMode && deleteSelectedIds.size > 0) ||
          reorderMode
        }
        actions={(() => {
          if (reorderMode) {
            return [{
              key: 'done', label: t('wal_done'), primary: true,
              icon: <IconCheck color="#fff" />,
              onClick: () => setReorderMode(false),
            }];
          }
          if (deleteMode) {
            const n = deleteSelectedIds.size;
            const list = [{
              key: 'del', label: n > 0 ? t('wal_delete_count', { count: n }) : t('delete'), primary: true,
              icon: <IconTrash color="#fff" />,
              disabled: n === 0,
              onClick: () => setShowDeleteConfirm(true),
            }];
            if (n > 0) {
              list.push({
                key: 'reset', label: t('wal_reset_selection'),
                icon: <IconReset color={T.text} />,
                onClick: () => setDeleteSelectedIds(new Set()),
              });
            }
            list.push({
              key: 'exit', label: t('wal_exit_mode'),
              icon: <IconX color={T.text} />,
              onClick: exitDeleteMode,
            });
            return list;
          }
          if (linkMode) {
            const list = [{
              key: 'link', label: t('wal_connect'), primary: true,
              icon: <IconLink color="#fff" />,
              disabled: !canLink,
              onClick: () => setShowLinkSheet(true),
            }];
            if (selectedCount > 0) {
              list.push({
                key: 'reset', label: t('wal_reset_selection'),
                icon: <IconReset color={T.text} />,
                onClick: () => clearSelection(),
              });
            }
            list.push({
              key: 'exit', label: t('wal_exit_mode'),
              icon: <IconX color={T.text} />,
              onClick: () => setLinkMode(false),
            });
            return list;
          }
          if (filterUnmatched) {
            const list = [];
            if (filteredTxs.length >= 2) {
              list.push({
                key: 'link', label: t('wal_link_transfers'), primary: true,
                icon: <IconLink color="#fff" />,
                onClick: () => setLinkMode(true),
              });
            }
            list.push({
              key: 'pickdel', label: t('wal_delete_individually'), danger: true,
              icon: <IconTrash color="#E4544A" />,
              onClick: () => { setLinkMode(false); setReorderMode(false); setDeleteMode(true); },
            });
            list.push({
              key: 'delete', label: t('wal_delete_filtered'), danger: true,
              icon: <IconTrash color="#E4544A" />,
              onClick: () => setShowDeleteConfirm(true),
            });
            return list;
          }
          // Normal-Modus
          const list = [];
          if (unmatchedCount > 0 || hasSuspiciousTimestamps) {
            list.push({
              key: 'sort', label: t('wal_sort'),
              icon: <IconSort color={T.text} />,
              onClick: () => { setLinkMode(false); setReorderMode(true); },
            });
          }
          list.push({
            key: 'pickdel', label: t('wal_delete_individually'),
            icon: <IconTrash color={T.text} />,
            onClick: () => { setLinkMode(false); setReorderMode(false); setDeleteMode(true); },
          });
          list.push({
            key: 'delete', label: filterText ? t('wal_delete_filtered') : t('wal_delete_all'), danger: true,
            icon: <IconTrash color="#E4544A" />,
            onClick: () => setShowDeleteConfirm(true),
          });
          return list;
        })()}
      />
      )}

      <DataCheckSheet
        open={dcOpen}
        onClose={() => setDcOpen(false)}
        data={dataCheck}
        onNavigate={null}
        onResolveFix={onOpenResolve}
      />
    </div>
  );
}

// ── Reorderbare Card (eigene Komponente für useDragControls-Hook) ─────────────

function ReorderableCard({ group, tx, id, onEdit, onDelete, transferMap, marketData, linkedTx }) {
  const [isDragging, setIsDragging] = useState(false);
  const dragControls = useDragControls();

  return (
    <Reorder.Item
      value={group}
      dragListener={false}
      dragControls={dragControls}
      onDragStart={() => setIsDragging(true)}
      onDragEnd={() => setIsDragging(false)}
      style={{ listStyle: 'none', outline: 'none' }}
      whileDrag={{
        scale: 1.025,
        rotate: 0.8,
        zIndex: 50,
        boxShadow: '0 28px 72px rgba(0,0,0,0.6), 0 8px 24px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.05)',
      }}
      transition={{ type: 'spring', stiffness: 500, damping: 35 }}
    >
      <TxCard
        tx={tx}
        isOpen={false}
        onToggle={() => {}}
        onEdit={onEdit}
        onDelete={onDelete}
        transferMap={transferMap}
        marketData={marketData}
        linkedTx={linkedTx}
        reorderable
        isDragging={isDragging}
        dragControls={dragControls}
      />
    </Reorder.Item>
  );
}

// ── Reorderbare Chain Card ────────────────────────────────────────────────────

function ReorderableChainCard({ group, txA, txB, marketData, onEdit, onDelete, onUnlink }) {
  const [isDragging, setIsDragging] = useState(false);

  return (
    <Reorder.Item
      value={group}
      onDragStart={() => setIsDragging(true)}
      onDragEnd={() => setIsDragging(false)}
      style={{ listStyle: 'none', outline: 'none', cursor: isDragging ? 'grabbing' : 'grab' }}
      whileDrag={{
        scale: 1.025,
        rotate: 0.8,
        zIndex: 50,
        boxShadow: '0 28px 72px rgba(0,0,0,0.6), 0 8px 24px rgba(0,0,0,0.35), 0 0 0 1px rgba(96,165,250,0.1)',
      }}
      transition={{ type: 'spring', stiffness: 500, damping: 35 }}
    >
      <TransferChainCard
        txA={txA}
        txB={txB}
        marketData={marketData}
        onEdit={onEdit}
        onDelete={onDelete}
        onUnlink={onUnlink}
        isDragging={isDragging}
      />
    </Reorder.Item>
  );
}

